/** MUI X date/time pickers, themed to the NexBasira dark palette.
 *
 * We pull in @mui/x-date-pickers purely for the calendar + analog-clock
 * popup widgets (the native <input type="date/datetime-local"> can't do
 * a 24h analog clock and renders inconsistently per-OS). Everything else
 * in the app stays Tailwind — these wrappers keep MUI contained to the
 * picker popups and expose the same string-in / string-out API the old
 * DateTime24Picker did, so call sites are drop-in.
 *
 * The ThemeProvider lives inside each wrapper so the popper (rendered in
 * a portal) still inherits the dark theme via React context.
 */
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
// Register the dayjs locales the picker adapter needs so the input mask +
// calendar follow the SELECTED UI language (not the browser locale) — e.g.
// French shows `JJ/MM/AAAA`, not `MM/DD/YYYY`. `en` is dayjs's built-in
// default. Each locale file is ~1 KB.
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/it";
import "dayjs/locale/de";
import "dayjs/locale/nl";
import "dayjs/locale/pt";
import "dayjs/locale/pl";
import "dayjs/locale/sv";
import "dayjs/locale/cs";
import "dayjs/locale/da";
import "dayjs/locale/fi";
import "dayjs/locale/nb";
import "dayjs/locale/ro";
import "dayjs/locale/el";

// UI locale code → dayjs locale id (identity except where they differ).
const DAYJS_LOCALE: Record<string, string> = { no: "nb" };

// Mirrors the --nb-* CSS tokens in src/index.css.
const NB = {
  bg: "#0A0E14",
  surface: "#1E2533",
  surface2: "#252D3D",
  border: "#2A3344",
  mint: "#00E5A0",
  text: "#E8ECF1",
  text2: "#A0A8B8",
};

const pickerTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: NB.mint, contrastText: "#04110C" },
    background: { default: NB.bg, paper: NB.surface },
    text: { primary: NB.text, secondary: NB.text2 },
    divider: NB.border,
  },
  shape: { borderRadius: 8 },
  typography: { fontFamily: "inherit", fontSize: 13 },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: NB.surface,
          color: NB.text,
          "& fieldset": { borderColor: NB.border },
          "&:hover fieldset": { borderColor: NB.mint },
          "&.Mui-focused fieldset": { borderColor: NB.mint, borderWidth: 1 },
        },
        input: { padding: "9px 12px", fontSize: 14 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: NB.surface, backgroundImage: "none" },
      },
    },
  },
});

export function MuiPickersProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const adapterLocale = DAYJS_LOCALE[lang] ?? lang;
  return (
    <ThemeProvider theme={pickerTheme}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={adapterLocale}
      >
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
}
